import React from 'react';
import PipelineAPI from '../../../api/pipeline';
import { ReviewCorrection } from '../../../api/structs';
import { ReviewProfileCheckpoint } from '../../../api/structs/reviewProfile';
import { ReviewerNode } from './graphTypes';
import { reviewScopeLabel } from './lessonScope';
import ReviewerNodeDetail from './ReviewerNodeDetail';
import ReviewerPromptSection from './ReviewerPromptSection';
import {
  detailChip,
  detailChipOn,
  detailHeading,
  detailMuted,
  detailRow,
} from './reviewerDetailStyles';

const ChipList: React.FC<{ items: string[]; on?: boolean }> = ({ items, on }) => (
  <div className="flex flex-wrap gap-1">
    {items.map((item) => (
      <span key={item} className={on ? detailChipOn : detailChip}>
        {item}
      </span>
    ))}
  </div>
);

/** What the judge rules on: where the criteria come from, and the fixed ones. */
const ChecksSection: React.FC<{ checkpoint: ReviewProfileCheckpoint }> = ({ checkpoint }) => {
  const { checks } = checkpoint;
  return (
    <section className="space-y-1.5" data-testid="reviewer-detail-checks">
      <h4 className={detailHeading}>Checks</h4>
      <p className={detailRow}>
        {`${checks.source || 'none'}${checks.per_frame ? ' · per frame' : ' · per candidate'}`}
      </p>
      {checks.context_key && (
        <p className={`${detailMuted} break-all`} title="where the criteria are read from">
          {checks.context_key}
        </p>
      )}
      {(checks.fixed || []).length > 0 && <ChipList items={checks.fixed || []} on />}
      {(checks.lenses || []).length > 0 && <ChipList items={checks.lenses || []} />}
      {!checks.context_key && !(checks.fixed || []).length && !(checks.lenses || []).length && (
        <p className={detailMuted}>nothing fixed — the judge rules on the planner’s criteria</p>
      )}
    </section>
  );
};

/** Which judge call runs on which model, and when a fail escalates. */
const ModelsSection: React.FC<{ checkpoint: ReviewProfileCheckpoint }> = ({ checkpoint }) => {
  const { gates } = checkpoint;
  return (
    <section className="space-y-1.5" data-testid="reviewer-detail-models">
      <h4 className={detailHeading}>Models</h4>
      {checkpoint.tiers.map((tier) => (
        <div key={tier.id} className="space-y-0.5" data-testid={`reviewer-model-${tier.id}`}>
          <p className={detailRow}>
            {`${tier.id} · ${tier.provider || 'no provider'} · ${tier.model || 'no model'}`}
          </p>
          {tier.inherited && (
            <p
              className={detailMuted}
              title="no layer of this checkpoint named it — it came down the cascade"
            >
              inherited
            </p>
          )}
        </div>
      ))}
      <p className={detailMuted} data-testid="reviewer-model-gates">
        {`escalates ≥ ${gates.confidence.toFixed(2)} · ${gates.severity} · ${gates.max_attempts}×${
          gates.inherited ? ' · inherited' : ''
        }`}
      </p>
    </section>
  );
};

/**
 * One correction, read as the judge reads it: the distilled RULE first, the
 * raw note that produced it underneath (quieter — it is evidence, not the
 * lesson), and how far the rule reaches. A correction saved without distilling
 * has no rule, so its note IS the line.
 *
 * The order is the backend's — this_checkpoint before this_pipeline before
 * all_pipelines, then weight, then newest — which is the order the judge is
 * shown them in. Re-sorting here would make the list disagree with the prompt.
 */
const LessonRow: React.FC<{ correction: ReviewCorrection }> = ({ correction }) => {
  const lesson = (correction.lesson || '').trim();
  const reason = (correction.reason || '').trim();
  const weight = correction.weight || 0;
  const label = correction.label.replace('_', ' ');
  return (
    <div className="space-y-0.5" data-testid="reviewer-lesson-row">
      <p className={detailRow}>{lesson || reason || label}</p>
      {lesson && reason && (
        <p className={detailMuted} data-testid="reviewer-lesson-reason">
          {reason}
        </p>
      )}
      <div className="flex flex-wrap items-center gap-1">
        <span className={detailChip} data-testid="reviewer-lesson-scope">
          {reviewScopeLabel(correction.scope, correction.model_id)}
        </span>
        {weight > 1 && (
          <span
            className={detailChipOn}
            data-testid="reviewer-lesson-weight"
            title="times this lesson has been recorded"
          >
            {`×${weight}`}
          </span>
        )}
        <span className={detailMuted}>
          {`${label} · ${new Date(correction.created_at).toLocaleDateString()}`}
        </span>
      </div>
    </div>
  );
};

/** What a human told this template's reviewer it got wrong. */
const LessonsSection: React.FC<{ templateId: string }> = ({ templateId }) => {
  const [corrections, setCorrections] = React.useState<ReviewCorrection[]>([]);
  const [error, setError] = React.useState('');

  React.useEffect(() => {
    if (!templateId) return undefined;
    let cancelled = false;
    const load = async () => {
      try {
        const data = await PipelineAPI.getReviewCorrections(templateId);
        if (!cancelled) setCorrections(data?.corrections || []);
      } catch (err) {
        if (!cancelled) {
          setCorrections([]);
          setError(err instanceof Error && err.message ? err.message : 'Could not read them.');
        }
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [templateId]);

  return (
    <section className="space-y-1.5" data-testid="reviewer-detail-lessons">
      <h4 className={detailHeading}>Lessons</h4>
      {error && <p className={detailMuted}>{error}</p>}
      {!error && corrections.length === 0 && (
        <p className={detailMuted} data-testid="reviewer-lessons-empty">
          Nothing corrected yet — “Wrong verdict” on a verdict teaches it.
        </p>
      )}
      {corrections.map((correction) => (
        <LessonRow key={correction.id} correction={correction} />
      ))}
    </section>
  );
};

export interface ReviewerDetailProps {
  checkpoint: ReviewProfileCheckpoint;
  /** The template whose corrections the Lessons section reads. */
  templateId: string;
  /** The clicked node, if any — its block sits above the four sections. */
  selectedNode?: ReviewerNode | null;
}

/**
 * The quiet column beside the picture: the judge's prompt and its slots, what
 * it rules on, which models it runs on, and what a human has corrected. The
 * canvas answers "what is wired to what"; this answers "and what does that
 * actually say".
 *
 * READ-ONLY throughout — every value here is written somewhere else (Prompt
 * templates, the checkpoint's review block, Settings → Reviewer), and offering
 * a second edit path would be a second source of truth.
 */
const ReviewerDetail: React.FC<ReviewerDetailProps> = ({
  checkpoint,
  templateId,
  selectedNode,
}) => (
  <div className="space-y-3" data-testid="reviewer-detail">
    {selectedNode && <ReviewerNodeDetail node={selectedNode} />}
    <ReviewerPromptSection checkpoint={checkpoint} />
    <ChecksSection checkpoint={checkpoint} />
    <ModelsSection checkpoint={checkpoint} />
    <LessonsSection templateId={templateId} />
  </div>
);

export default ReviewerDetail;
